'use client'

import React, { ChangeEvent, memo, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  Session,
  signOut,
  useSegmentCode,
  useSession,
  useTenant,
} from '@rewardops/auth/src/client'
import {
  Button,
  buttonVariants,
  cn,
  Divider,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@rewardops/ui'
import { timeLeftOnSession } from '@rewardops/utils'

import { AuthorizationUrlResponse } from '~/utils/auth-utils'
import { updateSession } from '~/utils/session-utils'
import { useApi } from '../hooks/use-api'
import useRefreshMemberPoints from '../hooks/use-refresh-member-points'

type AuthProviderOptions = {
  provider?: string
  redirect?: AuthorizationUrlResponse
}

const DevAuthButtons: React.FC = memo(() => {
  const { data: session } = useSession()
  const segmentCode = useSegmentCode()

  // Call /api/auth/provider to see what methods are available
  const [authProvider, setAuthProvider] = useState<
    AuthProviderOptions | undefined
  >(undefined)

  useEffect(() => {
    fetch('/api/auth/provider')
      .then((res) => res.json())
      .then((data) => {
        setAuthProvider(data)
      })
      .catch((error) => {
        console.error('Error fetching provider info', error)
      })
  }, [])

  const memberTags = session?.user?.member.memberTags
  const memberRole = session?.user?.member?.role

  return session?.user ? (
    <div className="flex w-28 flex-col gap-1">
      {segmentCode && (
        <div className="flex h-6 w-fit items-center justify-center rounded-full bg-emerald-600 p-3 font-mono text-xs text-white">
          {segmentCode}
        </div>
      )}
      {memberRole && (
        <div className="flex h-6 w-fit items-center justify-center rounded-full bg-tenant-primary-color p-3 font-mono text-xs text-white">
          {memberRole}
        </div>
      )}
      {memberTags && (
        <div className="flex h-6 w-fit items-center justify-center rounded-full bg-rose-600 p-3 font-mono text-xs text-white">
          {memberTags.length > 0 ? memberTags.join(', ') : '[]'}
        </div>
      )}
      <EditButton />
      <LogoutButton />
    </div>
  ) : (
    <LoginButtons authProvider={authProvider} />
  )
})

const LoginButtons = ({
  authProvider,
}: {
  authProvider?: AuthProviderOptions
}) => {
  const [accountId, setAccountId] = useState('') // Default value
  const [showError, setShowError] = useState(false)
  const [segmentCode, setSegmentCode] = useState<string>('')
  const [role, setRole] = useState<string>('')

  const tenant = useTenant()

  const handleMbnaClick = (e: React.MouseEvent) => {
    if (!accountId.trim()) {
      e.preventDefault()
      setShowError(true)
    } else {
      setShowError(false)
    }
  }

  const onSelectSegmentCode = (e: ChangeEvent<HTMLSelectElement>) => {
    setSegmentCode(e.target?.value)
  }

  const onSelectRole = (e: ChangeEvent<HTMLSelectElement>) => {
    setRole(e.target?.value)
  }

  const queryParams = new URLSearchParams({
    segmentCode,
    role,
  })

  return (
    <div className="w-50 flex flex-col gap-1">
      <a
        className={cn(buttonVariants({ size: 'sm' }))}
        href={`/api/auth/sso?${queryParams?.toString()}`}
      >
        Login
      </a>

      {/* mock login with other supported segments */}
      {tenant?.programConfig?.programSegments &&
      tenant?.programConfig?.programSegments?.length ? (
        <select
          className="flex w-full items-center rounded-md bg-primary p-2 text-center text-xs text-primary-foreground hover:cursor-pointer hover:bg-primary/90"
          onChange={onSelectSegmentCode}
          defaultValue=""
        >
          <option disabled hidden value="">
            Select segment
          </option>
          {tenant?.programConfig?.programSegments?.map((segment) => (
            <option key={segment} value={segment}>
              {segment}
            </option>
          ))}
        </select>
      ) : null}

      {tenant?.programConfig?.programRoles &&
      tenant?.programConfig?.programRoles?.length ? (
        <select
          className="flex w-full items-center rounded-md bg-primary p-2 text-center text-xs text-primary-foreground hover:cursor-pointer hover:bg-primary/90"
          onChange={onSelectRole}
          defaultValue=""
        >
          <option disabled hidden value="">
            Select Role
          </option>
          {tenant?.programConfig?.programRoles?.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
      ) : null}

      {authProvider && authProvider.provider === 'oidc' && (
        <a
          className={cn(buttonVariants({ size: 'sm' }))}
          href={authProvider.redirect?.url}
        >
          Use OIDC
        </a>
      )}
      {authProvider && authProvider.provider === 'mbna' && (
        <div
          className={cn(
            buttonVariants({ size: 'sm' }),
            'flex h-8 flex-col items-center justify-center gap-1 py-6',
          )}
        >
          <div className="flex items-center gap-2">
            <a
              href={`/auth/sso/mbna/dev-redirect/${accountId}`}
              onClick={handleMbnaClick}
            >
              MBNA Redirected Login
            </a>
            {showError && (
              <span className="text-xs text-red-500">
                Please enter an Account ID
              </span>
            )}
          </div>
          <Input
            className="h-4 rounded-sm text-xs text-black"
            value={accountId}
            onChange={(e) => {
              setAccountId(e.target.value)
              setShowError(false)
            }}
            placeholder="Account ID"
          />
        </div>
      )}
    </div>
  )
}

const LogoutButton = () => {
  const [timeLeft, setTimeLeft] = React.useState<string | undefined>(undefined)
  const { data: session, isLoading } = useSession()
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null)
  const tenant = useTenant()

  useEffect(() => {
    if (!isLoading && session) {
      if (timer) {
        clearInterval(timer)
      }
      setTimer(
        setInterval(() => {
          setTimeLeft(timeLeftOnSession(session))
        }, 1000),
      )
    }

    return () => {
      if (timer) {
        clearInterval(timer)
      }
    }
  }, [isLoading, session])

  return (
    <Button
      size={'sm'}
      onClick={() =>
        signOut({
          redirectUrl: window.location.href,
          logoutEndpoint: tenant?.logoutEndpoint,
        })
      }
    >
      Logout {timeLeft ? `(${timeLeft})` : ''}
    </Button>
  )
}

const EditButton = memo(() => {
  const { data: session, setTrigger: sessionTrigger } = useSession()
  const { register, handleSubmit } = useForm()
  const [popoverOpen, setPopoverOpen] = useState(false)

  const { api } = useApi()
  const { refreshPoints } = useRefreshMemberPoints()
  const sessionQuery = api.auth.getSession.useQuery()
  const pointsQuery = api.auth.getMemberPoints.useQuery()

  const cleanSession = async () => {
    await sessionQuery.refetch()
    await pointsQuery.refetch()
  }

  if (!session) {
    return null
  }

  const points = session.user?.member.points

  if (!points) {
    return null
  }

  const onSubmit = (data: any) => {
    updateSession('PATCH', { points: Number(data.points) }, sessionTrigger)
      .then(async (data: Session) => {
        await cleanSession()
        setPopoverOpen(false)
        console.log('Session Updated', data.user?.member)
      })
      .catch((error) => {
        console.error('Error updating session', error)
      })
  }

  return (
    <Popover
      open={popoverOpen}
      onOpenChange={(value) => {
        if (value === popoverOpen) return
        setPopoverOpen(value)
      }}
    >
      <PopoverTrigger>
        <Button size={'sm'} className="w-full">
          More...
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" side="right">
        <div className="grid gap-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none">Edit Member</h4>
            <code className="text-sm text-muted-foreground">
              ID: {session.user?.member.id}
            </code>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
            <div className="grid grid-cols-3 items-center gap-4">
              <Label htmlFor="width">Points</Label>
              <Input
                className="col-span-2 h-8"
                defaultValue={points}
                {...register('points')}
              />
            </div>
            <Divider />
            <Button size={'sm'} variant={'default'}>
              Save
            </Button>
          </form>
          <Divider />
          <Button
            size={'sm'}
            variant={'secondary'}
            onClick={async () => {
              refreshPoints()
              setPopoverOpen(false)
            }}
          >
            Refresh Points
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
})

export { DevAuthButtons }
